import { Chain, createPublicClient, http } from 'viem'

import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn } from '@reduxjs/toolkit/query'

import { Hash, Token } from 'types/entities'

import { ERC20 } from 'abis/ERC20'
import { Multicall } from 'abis/Multicall'
import { ETH_ADDRESS } from 'constants/addresses'
import { MULTICALL_ADDRESS } from 'constants/addresses'
import { fromWei, isEther, toChecksumAddress } from 'utils/crypto'

export type Params = {
  owner: Hash
  chain: Chain
  tokens: Token[]
  selfGenerated?: Hash
}

export type BalancesData = {
  bgBalance: string
  balance: string
}

export type Balances = Record<Hash, BalancesData>
export type AddressedBalances = Record<Hash, Balances>

const multicallBaseQuery = (): BaseQueryFn<Params, AddressedBalances, Error> => async (params) => {
  try {
    const existTokens = params.tokens.filter((_token) => Boolean(_token.address))
    const isEmptyTokens = existTokens.length === 0

    if (!params.owner || isEmptyTokens) {
      return { data: {} }
    }

    const erc20Tokens = params.tokens.filter((_token) => !isEther(_token.address))
    const client = createPublicClient({ chain: params.chain, transport: http() })

    const tokensMulticallArgs = erc20Tokens.map((_token) => {
      const contract = { address: _token.address, abi: ERC20 }
      return { ...contract, args: [params.owner], functionName: 'balanceOf' }
    })

    // ToDo remove gap params.selfGenerated || params.owner, use slice in final reduce
    const existingAddresses = [params.owner, params.selfGenerated || params.owner].filter((el) => el)

    const etherMulticallArgs = existingAddresses.map((address) => {
      const contract = { address: MULTICALL_ADDRESS, abi: Multicall }
      return { ...contract, args: [address], functionName: 'getEthBalance' }
    })

    const contracts = [...etherMulticallArgs, ...tokensMulticallArgs] as const

    // @ts-ignore
    const [ownerEthBalance, selfGeneratedEthBalance, ...tokenBalances] = await client.multicall({ contracts })

    const balances = tokenBalances.reduce<AddressedBalances>(
      (acc, curr, index) => {
        if (!curr.result) {
          return acc
        }
        const token = erc20Tokens[index]
        const _address = toChecksumAddress(token.address)
        acc[params.owner][_address] = {
          bgBalance: String(curr.result),
          balance: fromWei(String(curr.result), token.decimals).toString(),
        }
        return acc
      },
      {
        [params.owner]: {
          [ETH_ADDRESS]: {
            bgBalance: String(ownerEthBalance.result),
            balance: fromWei(String(ownerEthBalance.result)).toString(),
          },
        },
      },
    )

    if (params.selfGenerated) {
      balances[params.selfGenerated] = {
        [ETH_ADDRESS]: {
          bgBalance: String(selfGeneratedEthBalance.result),
          balance: fromWei(String(selfGeneratedEthBalance.result)).toString(),
        },
      }
    }

    return {
      data: balances,
    }
  } catch (error) {
    return { error }
  }
}

export const balancesApi = createApi({
  reducerPath: 'balances',
  baseQuery: multicallBaseQuery(),
  endpoints: (build) => ({
    getBalances: build.query<AddressedBalances, Params>({
      query: (params: Params) => params,
    }),
  }),
})

export const { useGetBalancesQuery } = balancesApi
